import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth-store';
import api from '../api';
import {
  SOURCE_LANGUAGE,
  SUPPORTED_LANGUAGES,
  getStoredLanguage,
  setStoredLanguage,
} from './i18n';

/**
 * Resolve the language a user should actually see:
 *   personal override (User.language) → organization default → source language.
 * A `null`/absent personal value means "follow the org default", which is how
 * an org-wide default reaches every user automatically.
 */
function effectiveLanguage(
  userLanguage: string | null | undefined,
  orgDefault: string | null | undefined,
): string {
  return userLanguage || orgDefault || SOURCE_LANGUAGE;
}

/**
 * Read/set the active UI language. A user's pick is a personal override that is
 * persisted to `User.language` (so it follows them across devices) and wins over
 * the org default; clearing it (resetToOrgDefault) reverts to the org default.
 */
export function useLanguagePreference(): {
  language: string;
  setLanguage: (lang: string) => void;
  resetToOrgDefault: () => void;
  supported: typeof SUPPORTED_LANGUAGES;
  orgDefault: string;
  isUsingOrgDefault: boolean;
} {
  const { i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const orgDefault = user?.organization?.defaultLanguage || SOURCE_LANGUAGE;
  const language = i18n.language || getStoredLanguage();
  // The user "follows the org default" whenever they have no personal override.
  const isUsingOrgDefault = !user?.language;

  // Persist the per-user override to the server. A supported code sets it; `null`
  // clears it (revert to org default). The local switch already happened for a
  // snappy UI, so a failed write simply isn't remembered server-side.
  const persist = useCallback(
    async (lang: string | null) => {
      try {
        await api.patch('/users/me/profile', { language: lang });
      } catch {
        /* best-effort: keep the local choice even if persistence fails */
      }
      const current = useAuthStore.getState().user;
      if (current) setUser({ ...current, language: lang ?? undefined });
    },
    [setUser],
  );

  const setLanguage = useCallback(
    (lang: string) => {
      setStoredLanguage(lang);
      void persist(lang);
    },
    [persist],
  );

  const resetToOrgDefault = useCallback(() => {
    setStoredLanguage(effectiveLanguage(null, orgDefault));
    void persist(null);
  }, [orgDefault, persist]);

  return {
    language,
    setLanguage,
    resetToOrgDefault,
    supported: SUPPORTED_LANGUAGES,
    orgDefault,
    isUsingOrgDefault,
  };
}

/**
 * Apply the signed-in user's EFFECTIVE language once the bootstrap payload is
 * known — personal override if set, otherwise the organization's default,
 * otherwise the source language. Mounted once high in the authenticated tree
 * (AppBootstrap). This is what makes an org-wide default apply to every user
 * automatically while still letting an individual override it for themselves.
 */
export function useSyncUserLanguage(): void {
  const hasUser = useAuthStore((s) => s.user != null);
  const userLanguage = useAuthStore((s) => s.user?.language);
  const orgDefault = useAuthStore((s) => s.user?.organization?.defaultLanguage);
  useEffect(() => {
    if (!hasUser) return;
    const effective = effectiveLanguage(userLanguage, orgDefault);
    if (effective && effective !== getStoredLanguage()) {
      setStoredLanguage(effective);
    }
  }, [hasUser, userLanguage, orgDefault]);
}
